//
// The logo a company new to Paxity starts with, and the confirmation of the
// company itself (/invite/confirmGovId), which creates it. A company already on
// Paxity keeps its own logo: the server drops one sent for it.
//
// The confirmation may answer another portal: the company lives on another
// server, and the invitee goes on there.
//
import {
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {
  Button,
  CheckeredImageFrame,
  Column,
  Row,
  useWizardContext,
} from '../../../components';
import {
  useAuth,
  useTownAssets,
} from '../../../stores';
import {
  compressedLogoBase64,
  config,
  logError,
} from '../../../utils';

// The image types the customer logo picker accepts, so this logo is one the
// company could have set from its own settings page
const logoFileTypes = 'image/bmp, image/gif, image/jpeg, image/png, image/tiff, '
  + 'image/webp, image/x-windows-bmp, image/svg+xml';

const pageStateValidator = (state) => ({
  valid: {},
  errors: {},
  pageValid: Boolean(state.cstmrGovId),
});

// The server's refusal flags, as the sentence shown for each
const refusalKeyOf = (data) => {
  if (data?.customerExists) return 'inviteCustomerExists';
  if (data?.companyNotFound) return 'govIdNotFound';
  if (data?.confirmInProgress) return 'finalizeInProgress';
  if (data?.invalidStatus || data?.govIdLocked) return 'inviteStatusError';
  return 'inviteConfirmCompanyError';
};

function InviteAckLogoPage() {
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { auth } = useAuth();
  const { superagentPostAsync } = auth;
  const {
    setPageInfo,
    setPageState,
    setPageBusy,
    goToPage,
    state,
  } = useWizardContext();

  // The confirmation reads the wizard as it is when clicked
  const stateRef = useRef(state);
  stateRef.current = state;

  const confirmPage = useCallback(
    async () => {
      const {
        piid,
        cstmrGovId,
        initLogo,
        onCompanyConfirmed,
      } = stateRef.current;

      setPageBusy(true);
      setPageState({ confirmRefusalKey: undefined }, false);

      let data;
      try {
        const res = await superagentPostAsync(
          config.town.back.urlPrefix + config.town.back.invite.confirmGovId,
          ['Content-Type', 'application/json'],
          {
            piid,
            cstmrGovId,
            ...((initLogo) ? { initLogo } : {}),
          },
        );

        data = (res?.status === 200) ? res.body?.rslt?.data?.[0] : undefined;
      } catch (err) {
        logError(
          `confirmGovId ${err?.message ?? err}`,
          'InviteAckLogoPage.confirmGovId.error',
          { piid },
        );
      }

      if (data?.cstmrWww) {
        // Another server takes over from here; this page is left behind
        if (!onCompanyConfirmed(data.cstmrWww)) goToPage('identity');

        return false;
      }

      setPageState({ confirmRefusalKey: refusalKeyOf(data) }, false);
      setPageBusy(false);

      return false;
    },
    [superagentPostAsync, setPageState, setPageBusy, goToPage],
  );

  useEffect(
    () => {
      setPageInfo({
        prevPage: 'company',
        pageStateValidator,
        confirmPage,
        confirmLabel: translate('confirmCompany'),
      });
    },
    [setPageInfo, confirmPage, translate],
  );

  const onLogoFile = useCallback(
    async (event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;

      event.target.value = null;
      setPageBusy(true);

      let initLogo;
      try {
        initLogo = await compressedLogoBase64(file);
      } catch (err) {
        logError(
          `logo upload ${err?.message ?? err}`,
          'InviteAckLogoPage.logoUpload.error',
          { type: file.type },
        );
      }

      if (initLogo) setPageState({ initLogo }, false);
      setPageBusy(false);
    },
    [setPageState, setPageBusy],
  );

  return (
    <Column crossStretch gap={4} className="flex-1 min-h-0 w-full overflow-y-auto p-2">
      <span>{translate('inviteLogoProposal', 'statements')}</span>

      <Row gap={4} crossStart>
        {(state.initLogo)
          ? (
              <CheckeredImageFrame>
                <img
                  src={`data:${state.initLogo.mime};base64,${state.initLogo.base64}`}
                  alt={translate('logo')}
                  className="h-full w-full object-contain"
                />
              </CheckeredImageFrame>
            )
          : null}

        <Column crossStart gap={2}>
          <Button
            id="uploadLogo"
            label={translate('uploadALogo')}
            fileOnInput={onLogoFile}
            accept={logoFileTypes}
            neutralColor
          />

          {(state.initLogo)
            ? (
                <Button
                  id="removeLogo"
                  label={translate('remove')}
                  onClick={() => setPageState({ initLogo: undefined }, false)}
                  neutralColor
                  tiny
                />
              )
            : null}
        </Column>
      </Row>

      {(state.confirmRefusalKey)
        ? <span className="text-pxUiAlert italic">{translate(state.confirmRefusalKey, 'statements')}</span>
        : null}
    </Column>
  );
}

export default InviteAckLogoPage;
